/* Demande sur mesure : recherche TMDB → « C'est bien ça ? » (boîtier avec l'affiche) → saisons
   → « Ajouter un autre film ou une autre série » (spin-off…) → ajout au panier du produit « Demande sur mesure »
   (une ligne par disque, détails en propriétés) → paiement. Le paiement est seulement réservé : il est encaissé
   quand la demande est acceptée. */
if (!customElements.get('db-request-builder')) {
  customElements.define(
    'db-request-builder',
    class DbRequestBuilder extends HTMLElement {
      // Prix d'un disque (une saison ou un film) : 30 € le 1er, 25 € les suivants, 20 € à partir du 4e quand la demande
      // compte 5 disques ou plus ; +5 € au-delà de 13 épisodes, +10 € au-delà de 26.
      // Doit rester identique aux variantes du produit « Demande sur mesure » (outils/jaquettes : demande-init).
      static supplement(episodes) {
        if (episodes > 26) return 10;
        if (episodes > 13) return 5;
        return 0;
      }

      static price(rank, count, episodes) {
        const base = rank === 0 ? 30 : rank >= 3 && count >= 5 ? 20 : 25;
        return base + DbRequestBuilder.supplement(episodes);
      }

      static IMG = 'https://image.tmdb.org/t/p/';

      static normalize(text) {
        return (text || '')
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      // Clés d'identification sans compte : ordre des mots, accents, majuscules et fautes proches à l'oreille tolérés.
      // Doit rester identique à nameKeys (outils/jaquettes/jaquettes.mjs), qui publie ces clés hachées.
      static phonetic(t) {
        const s = t.replace(/ph/g, 'f').replace(/qu/g, 'k').replace(/c([eiy])/g, 's$1').replace(/[cq]/g, 'k').replace(/z/g, 's').replace(/y/g, 'i').replace(/w/g, 'v').replace(/h/g, '').replace(/(.)\1+/g, '$1').replace(/[estdx]$/, '');
        return (s[0] || '') + s.slice(1).replace(/[aeiou]/g, '').replace(/(.)\1+/g, '$1');
      }

      static nameKeys(full) {
        const tokens = DbRequestBuilder.normalize(full).split(' ').filter((t) => t.length >= 2);
        if (tokens.length < 2) return [];
        const sets = [tokens];
        if (tokens.length > 2) sets.push([tokens[0], tokens[tokens.length - 1]]);
        return [...new Set(sets.flatMap((s) => [`n:${[...s].sort().join(' ')}`, `p:${s.map(DbRequestBuilder.phonetic).sort().join(' ')}`]))];
      }

      static async hashKey(key) {
        const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`disquebleu|${key}`));
        return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('').slice(0, 20);
      }

      static esc(text) {
        const div = document.createElement('div');
        div.textContent = text == null ? '' : String(text);
        return div.innerHTML;
      }

      connectedCallback() {
        this.key = this.dataset.tmdbKey;
        this.variants = JSON.parse(this.dataset.variants || '{}');
        // disques choisis, toutes séries confondues, dans l'ordre d'ajout : clé « type:id:saison »
        this.selected = new Map();
        this.money = (n) => `${n.toFixed(2).replace('.', ',')} €`;
        this.input = this.querySelector('[data-search]');
        this.results = this.querySelector('[data-results]');
        this.confirm = this.querySelector('[data-confirm]');
        this.seasons = this.querySelector('[data-seasons]');
        this.summary = this.querySelector('[data-summary]');
        this.review = this.querySelector('[data-step="review"]');
        this.mineBox = this.querySelector('[data-mine]');
        this.requests = JSON.parse(this.querySelector('[data-requests]')?.textContent || 'null') || {};
        // demandes déjà faites par la personne identifiée : clé « type:id:saison » → entrée
        this.mine = new Map();
        this.querySelector('[data-who]').addEventListener('submit', (event) => {
          event.preventDefault();
          this.identify(event.target);
        });
        let timer;
        this.input.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.search(this.input.value.trim()), 300);
        });
        this.addEventListener('click', (event) => {
          const pick = event.target.closest('[data-pick]');
          if (pick) return this.pick(pick.dataset.pick);
          const remove = event.target.closest('[data-remove]');
          if (remove) {
            this.selected.delete(remove.dataset.remove);
            if (!this.review.hidden) return this.selected.size ? this.renderReview() : this.reset(true);
            return this.item && !this.seasons.hidden ? this.renderSeasons() : this.renderSummary();
          }
          if (event.target.closest('[data-change]')) return this.step('who');
          if (event.target.closest('[data-review]')) return this.renderReview();
          if (event.target.closest('[data-edit]')) return this.item && this.item.seasons ? (this.step('seasons'), this.renderSeasons()) : this.reset(true);
          if (event.target.closest('[data-yes]')) return this.showSeasons();
          if (event.target.closest('[data-no]')) return this.reset(true);
          if (event.target.closest('[data-another]')) return this.reset(true, true);
          if (event.target.closest('[data-send]')) return this.send(event.target.closest('[data-send]'));
        });
        this.addEventListener('change', (event) => {
          if (!event.target.matches('[data-season]')) return;
          const s = this.item.seasons.find((x) => String(x.season_number) === event.target.value);
          const key = this.keyOf(this.item, s);
          if (event.target.checked) this.selected.set(key, { item: this.item, season: s });
          else this.selected.delete(key);
          this.renderSeasons();
        });
      }

      keyOf(item, season) {
        return `${item.type}:${item.id}:${season.season_number}`;
      }

      tmdb(path, params = {}) {
        const query = new URLSearchParams({ api_key: this.key, language: 'fr-FR', ...params });
        return fetch(`https://api.themoviedb.org/3/${path}?${query}`).then((r) => {
          if (!r.ok) throw new Error(`TMDB ${r.status}`);
          return r.json();
        });
      }

      // Identification sans compte : prénom + nom obligatoires, puis liste des demandes déjà faites à ce nom
      async identify(form) {
        const first = form.elements.first.value.trim();
        const last = form.elements.last.value.trim();
        const keys = DbRequestBuilder.nameKeys(`${first} ${last}`);
        const valid = DbRequestBuilder.normalize(first).length >= 2 && DbRequestBuilder.normalize(last).length >= 2 && keys.length;
        form.querySelector('[data-who-error]').hidden = Boolean(valid);
        if (!valid) return;
        this.person = `${first} ${last}`;
        this.mine = new Map();
        // une demande refusée n'écrase jamais une demande en cours pour le même disque
        const add = (entry) => {
          const prev = this.mine.get(entry.k);
          if (!prev || prev.st === 'refusee') this.mine.set(entry.k, entry);
        };
        try {
          this.hashes = await Promise.all(keys.map(DbRequestBuilder.hashKey));
          for (const h of this.hashes) (this.requests[h] || []).forEach(add);
          await this.addRecent(add);
        } catch (error) {
          // hachage indisponible (navigateur ancien) : on continue sans la liste
        }
        this.mineBox.querySelector('[data-hello]').textContent = this.dataset.hello.replace('[prenom]', first);
        this.mineBox.querySelector('[data-mine-list]').innerHTML = Array.from(this.mine.values())
          .map((e) => {
            const season = Number(e.k.split(':')[2]);
            return `<li class="db-builder__mine-item">
              ${e.a ? `<img src="${DbRequestBuilder.esc(e.a)}" alt="" loading="lazy" width="62" height="93">` : ''}
              <span><strong>${DbRequestBuilder.esc(e.t)}</strong>${season ? `<small>${this.dataset.labelSeason} ${season}</small>` : ''}
              <small class="db-builder__status is-${e.st}">${this.dataset[`status${e.st[0].toUpperCase()}${e.st.slice(1)}`] || ''} · ${DbRequestBuilder.esc(e.o)}</small></span>
            </li>`;
          })
          .join('');
        this.mineBox.querySelector('[data-mine-none]').hidden = this.mine.size > 0;
        this.reset(true);
      }

      // Demandes envoyées depuis ce navigateur, pas encore dans la liste publiée (mise à jour toutes les 15 min) :
      // retenues un jour, et seulement si elles ne sont plus dans le panier (paiement abandonné = pas une demande)
      static RECENT = 'db-demandes-recentes';

      async addRecent(add) {
        let recent = [];
        try {
          recent = JSON.parse(localStorage.getItem(DbRequestBuilder.RECENT) || '[]').filter((r) => Date.now() - r.at < 864e5);
        } catch (error) {
          return;
        }
        const mine = recent.filter((r) => r.h.some((h) => this.hashes.includes(h)));
        if (!mine.length) return;
        const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json()).catch(() => ({ items: [] }));
        const inCart = new Set(cart.items.map((i) => i.properties && i.properties._tmdb).filter(Boolean).map((t) => (t.split(':').length === 2 ? `${t}:0` : t)));
        mine.filter((r) => !inCart.has(r.k)).forEach((r) => add({ k: r.k, t: r.t, a: r.a, o: this.dataset.labelRecent, st: 'attente' }));
      }

      saveRecent(lines) {
        try {
          const recent = JSON.parse(localStorage.getItem(DbRequestBuilder.RECENT) || '[]').filter((r) => Date.now() - r.at < 864e5);
          lines.forEach((line) => recent.push({ k: this.keyOf(line.item, line.season), t: line.item.name, a: `${DbRequestBuilder.IMG}w342${line.season.poster_path || line.item.poster}`, h: this.hashes || [], at: Date.now() }));
          localStorage.setItem(DbRequestBuilder.RECENT, JSON.stringify(recent));
        } catch (error) {
          // stockage bloqué (navigation privée) : la liste publiée prendra le relais
        }
      }

      // déjà demandé par cette personne (en attente, acceptée, en création ou expédiée) : on ne le redemande pas
      already(item, season) {
        const entry = this.mine.get(`${item.type}:${item.id}:${item.type === 'movie' ? 0 : season.season_number}`);
        return entry && entry.st !== 'refusee' ? entry : null;
      }

      step(name) {
        this.querySelectorAll('[data-step]').forEach((el) => {
          // le récapitulatif reste visible dès qu'un disque est choisi, même pendant la recherche suivante
          el.hidden = el.dataset.step === 'summary' ? (!this.selected.size && name !== 'seasons') || name === 'review' || name === 'who' : el.dataset.step !== name;
        });
        this.mineBox.hidden = name === 'who' || name === 'review' || !this.person;
        this.querySelectorAll('[data-step-dot]').forEach((dot) => {
          const order = ['who', 'search', 'confirm', 'seasons', 'review'];
          dot.classList.toggle('is-done', order.indexOf(dot.dataset.stepDot) < order.indexOf(name));
          dot.classList.toggle('is-active', dot.dataset.stepDot === name);
        });
      }

      reset(focus, another = false) {
        this.item = null;
        if (another) {
          this.input.value = '';
          this.results.innerHTML = '';
        }
        this.step('search');
        this.renderSummary();
        if (focus) this.input.focus();
      }

      async search(query) {
        if (query.length < 2) return (this.results.innerHTML = '');
        this.results.innerHTML = `<li class="db-builder__note">${this.dataset.loading}</li>`;
        try {
          const data = await this.tmdb('search/multi', { query, include_adult: 'false' });
          const list = data.results.filter((r) => (r.media_type === 'tv' || r.media_type === 'movie') && r.poster_path).slice(0, 8);
          this.found = new Map(list.map((r) => [`${r.media_type}:${r.id}`, r]));
          this.results.innerHTML = list.length
            ? list
                .map((r) => {
                  const year = (r.first_air_date || r.release_date || '').slice(0, 4);
                  const kind = r.media_type === 'tv' ? this.dataset.labelTv : this.dataset.labelMovie;
                  return `<li><button type="button" class="db-builder__result" data-pick="${r.media_type}:${r.id}">
                    <img src="${DbRequestBuilder.IMG}w185${r.poster_path}" alt="" loading="lazy" width="92" height="138">
                    <span><strong>${DbRequestBuilder.esc(r.name || r.title)}</strong><small>${kind}${year ? ` · ${year}` : ''}</small></span>
                  </button></li>`;
                })
                .join('')
            : `<li class="db-builder__note">${this.dataset.empty}</li>`;
        } catch (error) {
          this.results.innerHTML = `<li class="db-builder__note">${this.dataset.error}</li>`;
        }
      }

      async pick(key) {
        const found = this.found.get(key);
        const [type, id] = key.split(':');
        this.item = { type, id, name: found.name || found.title, original: found.original_name || found.original_title, poster: found.poster_path };
        this.confirm.querySelector('[data-confirm-title]').textContent = this.item.name;
        this.confirm.querySelector('[data-confirm-case]').style.setProperty('--db-wrap', `url('${DbRequestBuilder.IMG}w780${found.poster_path}')`);
        this.confirm.querySelector('[data-confirm-meta]').textContent = `${type === 'tv' ? this.dataset.labelTv : this.dataset.labelMovie} · ${(found.first_air_date || found.release_date || '').slice(0, 4)}`;
        this.step('confirm');
        // en parallèle : saisons (séries) et éditions déjà en boutique
        this.details = type === 'tv' ? this.tmdb(`tv/${id}`) : Promise.resolve(null);
        this.catalog = this.findInCatalog(this.item);
      }

      // Éditions déjà en boutique pour ce titre : { saison → produit } (saison 0 = film ou édition unique)
      async findInCatalog(item) {
        const names = [item.name, item.original].map(DbRequestBuilder.normalize).filter(Boolean);
        const params = new URLSearchParams({ q: item.name, 'resources[type]': 'product', 'resources[limit]': '10' });
        const map = new Map();
        try {
          const data = await fetch(`${window.routes.predictive_search_url}.json?${params}`).then((r) => r.json());
          for (const p of data.resources.results.products) {
            if (p.handle === 'demande-sur-mesure') continue;
            const title = p.title.replace(/\s*[–—-]\s*blu[\s-]?ray.*$/i, '');
            const core = DbRequestBuilder.normalize(title.replace(/\s+[–—-]\s+.*$/, '').replace(/\s+(S\d+.*|Saison\s*\d+.*|Int[ée]grale.*)$/i, ''));
            // « Star Wars : Andor » correspond à « Andor », mais « Daredevil Born Again » ne correspond pas à « Daredevil »
            const same = names.some((n) => core === n || core.endsWith(` ${n}`) || n.endsWith(` ${core}`));
            if (!same) continue;
            const range = title.match(/S(?:aison)?\s*(\d+)\s*(?:à|a|et|-)\s*(\d+)/i);
            const single = title.match(/\bS(\d+)\b|Saison\s*(\d+)/i);
            const seasons = range ? Array.from({ length: range[2] - range[1] + 1 }, (_, i) => Number(range[1]) + i) : single ? [Number(single[1] || single[2])] : [0];
            seasons.forEach((s) => map.has(s) || map.set(s, p));
          }
        } catch (error) {
          // recherche indisponible : on laisse faire la demande
        }
        return map;
      }

      async showSeasons() {
        this.step('seasons');
        this.seasons.innerHTML = `<p class="db-builder__note">${this.dataset.loading}</p>`;
        const [details, catalog] = await Promise.all([this.details, this.catalog]);
        this.item.catalog = catalog;
        if (this.item.type === 'movie') {
          this.item.seasons = [{ season_number: 0, name: this.item.name, episode_count: 0, poster_path: this.item.poster }];
          // un film n'a qu'un disque : coché d'office (sauf s'il est déjà en boutique)
          if (!catalog.has(0) && !this.already(this.item, this.item.seasons[0])) this.selected.set(this.keyOf(this.item, this.item.seasons[0]), { item: this.item, season: this.item.seasons[0] });
        } else {
          this.item.seasons = (details.seasons || []).filter((s) => s.season_number > 0 && s.episode_count > 0);
        }
        this.renderSeasons();
      }

      // prix de chaque disque choisi, selon son rang dans toute la demande
      priced() {
        const list = Array.from(this.selected.entries());
        return list.map(([key, entry], rank) => ({ key, ...entry, price: entry.item.type === 'movie' ? DbRequestBuilder.price(rank, list.length, 0) : DbRequestBuilder.price(rank, list.length, entry.season.episode_count) }));
      }

      renderSeasons() {
        const lines = this.priced();
        const count = lines.length;
        const rows = this.item.seasons.map((s) => {
          const product = this.item.catalog.get(s.season_number) || (this.item.type === 'movie' ? this.item.catalog.get(0) : null);
          const label = this.item.type === 'movie' ? this.item.name : `${this.dataset.labelSeason} ${s.season_number}`;
          const meta = this.item.type === 'movie' ? this.dataset.labelMovie : `${(s.air_date || '').slice(0, 4)} · ${s.episode_count} ${this.dataset.labelEpisodes}`;
          const img = s.poster_path || this.item.poster;
          if (product) {
            return `<li class="db-builder__season is-available">
              <img src="${DbRequestBuilder.IMG}w154${img}" alt="" loading="lazy" width="62" height="93">
              <span class="db-builder__season-text"><strong>${DbRequestBuilder.esc(label)}</strong><small>${this.dataset.labelAvailable}</small></span>
              <a class="button button--small" href="${product.url.split('?')[0]}">${this.dataset.labelView}</a>
            </li>`;
          }
          const done = this.already(this.item, s);
          if (done) {
            return `<li class="db-builder__season is-available is-requested">
              <img src="${DbRequestBuilder.IMG}w154${img}" alt="" loading="lazy" width="62" height="93">
              <span class="db-builder__season-text"><strong>${DbRequestBuilder.esc(label)}</strong><small>${this.dataset.labelAlready.replace('[commande]', DbRequestBuilder.esc(done.o))}</small></span>
              <span class="db-builder__status is-${done.st}">${this.dataset[`status${done.st[0].toUpperCase()}${done.st.slice(1)}`] || ''}</span>
            </li>`;
          }
          const line = lines.find((l) => l.key === this.keyOf(this.item, s));
          // cochée : son prix dans la demande ; sinon : son prix si on l'ajoutait maintenant
          const price = line ? line.price : DbRequestBuilder.price(count, count + 1, this.item.type === 'movie' ? 0 : s.episode_count);
          return `<li class="db-builder__season${line ? ' is-checked' : ''}">
            <label>
              <input type="checkbox" data-season value="${s.season_number}"${line ? ' checked' : ''}>
              <img src="${DbRequestBuilder.IMG}w154${img}" alt="" loading="lazy" width="62" height="93">
              <span class="db-builder__season-text"><strong>${DbRequestBuilder.esc(label)}</strong><small>${DbRequestBuilder.esc(meta)}</small></span>
              <span class="db-builder__season-price">${this.money(price)}</span>
            </label>
          </li>`;
        });
        const instock = this.item.catalog.size ? `<p class="db-builder__instock">${this.dataset.labelInstock}</p>` : '';
        this.seasons.innerHTML = `${instock}<ul class="db-builder__season-list" role="list">${rows.join('')}</ul>`;
        this.renderSummary();
      }

      renderSummary() {
        const lines = this.priced();
        const total = lines.reduce((sum, line) => sum + line.price, 0);
        this.summary.hidden = !lines.length && !(this.item && this.item.seasons);
        this.summary.querySelector('[data-lines]').innerHTML = lines
          .map((line) => {
            const label = line.item.type === 'movie' ? line.item.name : `${line.item.name} · ${this.dataset.labelSeason} ${line.season.season_number}`;
            return `<li><span>${DbRequestBuilder.esc(label)}</span><span>${this.money(line.price)} <button type="button" class="db-builder__remove" data-remove="${line.key}" aria-label="${this.dataset.labelRemove} ${DbRequestBuilder.esc(label)}">×</button></span></li>`;
          })
          .join('');
        this.summary.querySelector('[data-total]').textContent = this.money(total);
        // saison suivante de la série en cours, à son prix une fois ajoutée
        const next = this.item && this.item.type === 'tv' && this.item.seasons && lines.length && this.item.seasons.find((s) => !this.item.catalog.has(s.season_number) && !this.already(this.item, s) && !this.selected.has(this.keyOf(this.item, s)));
        const upsell = this.summary.querySelector('[data-upsell]');
        upsell.hidden = !next;
        if (next) {
          upsell.textContent = this.dataset.upsellText
            .replace('[saison]', `${this.dataset.labelSeason} ${next.season_number}`)
            .replace('[prix]', this.money(DbRequestBuilder.price(lines.length, lines.length + 1, next.episode_count)));
        }
        this.summary.querySelector('[data-review]').disabled = !lines.length;
      }

      // Récapitulatif avant paiement : chaque disque avec son affiche, retirable, puis confirmation
      renderReview() {
        const lines = this.priced();
        this.step('review');
        this.review.querySelector('[data-review-list]').innerHTML = lines
          .map((line) => {
            const label = line.item.type === 'movie' ? this.dataset.labelMovie : `${this.dataset.labelSeason} ${line.season.season_number}`;
            return `<li class="db-builder__mine-item">
              <img src="${DbRequestBuilder.IMG}w154${line.season.poster_path || line.item.poster}" alt="" loading="lazy" width="62" height="93">
              <span><strong>${DbRequestBuilder.esc(line.item.name)}</strong><small>${DbRequestBuilder.esc(label)}</small></span>
              <span class="db-builder__season-price">${this.money(line.price)} <button type="button" class="db-builder__remove" data-remove="${line.key}" aria-label="${this.dataset.labelRemove} ${DbRequestBuilder.esc(line.item.name)}">×</button></span>
            </li>`;
          })
          .join('');
        this.review.querySelector('[data-review-total]').textContent = this.money(lines.reduce((sum, line) => sum + line.price, 0));
        this.review.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      async send(button) {
        const lines = this.priced();
        if (!lines.length || lines.some((line) => this.already(line.item, line.season))) return;
        button.disabled = true;
        button.classList.add('loading');
        const note = this.review.querySelector('[data-note]').value.trim();
        const items = lines.map((line, i) => {
          const properties = { Titre: line.item.name };
          if (line.item.type === 'tv') {
            properties[this.dataset.propSeason] = String(line.season.season_number);
            properties[this.dataset.propEpisodes] = String(line.season.episode_count);
          } else {
            properties.Type = this.dataset.labelMovie;
          }
          if (note && i === 0) properties[this.dataset.propNote] = note;
          properties._tmdb = `${line.item.type}:${line.item.id}${line.item.type === 'tv' ? `:${line.season.season_number}` : ''}`;
          properties._affiche = `${DbRequestBuilder.IMG}w342${line.season.poster_path || line.item.poster}`;
          // nom saisi à l'identification : retrouve ses demandes même si la commande est à un autre nom
          if (this.person) properties._nom = this.person;
          return { id: Number(this.variants[line.price]), quantity: 1, properties };
        });
        try {
          if (items.some((item) => !item.id)) throw new Error('tarif');
          const response = await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          }).then((r) => r.json());
          if (response.status) throw new Error(response.description);
          this.saveRecent(lines);
          window.location = '/checkout';
        } catch (error) {
          this.review.querySelector('[data-error]').hidden = false;
          button.disabled = false;
          button.classList.remove('loading');
        }
      }
    }
  );
}

/* Suivre ma demande sans compte : prénom + nom → demandes regroupées par commande, avec leur étape ou le motif du refus. */
if (!customElements.get('db-request-track')) {
  customElements.define(
    'db-request-track',
    class DbRequestTrack extends HTMLElement {
      connectedCallback() {
        this.requests = JSON.parse(this.querySelector('[data-requests]').textContent || 'null') || {};
        this.querySelector('[data-who]').addEventListener('submit', (event) => {
          event.preventDefault();
          this.lookup(event.target);
        });
      }

      async lookup(form) {
        const B = customElements.get('db-request-builder');
        const first = form.elements.first.value.trim();
        const last = form.elements.last.value.trim();
        const keys = B.nameKeys(`${first} ${last}`);
        const valid = B.normalize(first).length >= 2 && B.normalize(last).length >= 2 && keys.length;
        form.querySelector('[data-who-error]').hidden = Boolean(valid);
        if (!valid) return;
        const orders = new Map();
        for (const h of await Promise.all(keys.map(B.hashKey))) {
          for (const e of this.requests[h] || []) {
            const order = orders.get(e.o) || orders.set(e.o, { name: e.o, d: e.d, st: e.st, r: e.r, lines: new Map() }).get(e.o);
            order.lines.set(e.k, e);
          }
        }
        const steps = this.dataset.steps.split('|');
        const STEP = { attente: 1, acceptee: 2, fabrication: 3, expediee: 4 };
        const list = Array.from(orders.values()).sort((a, b) => (b.d || '').localeCompare(a.d || ''));
        this.querySelector('[data-results]').innerHTML = list
          .map((o) => {
            const lines = Array.from(o.lines.values())
              .map((e) => {
                const season = Number(e.k.split(':')[2]);
                return `<li class="db-track__line">${e.a ? `<img src="${B.esc(e.a)}" alt="" width="46" height="69" loading="lazy">` : ''}<span>${B.esc(e.t)}${season ? ` · ${this.dataset.season} ${season}` : ''}</span></li>`;
              })
              .join('');
            const body =
              o.st === 'refusee'
                ? `<p class="db-track__refused">${this.dataset.refused}</p><p>${this.dataset[{ introuvable: 'reasonIntrouvable', qualite: 'reasonQualite', 'trop-long': 'reasonTropLong' }[o.r] || 'reasonOther']}</p><a class="button button--secondary" href="${this.dataset.browseUrl}">${this.dataset.browse}</a>`
                : `<ol class="db-track__steps">${steps.map((label, i) => `<li class="${i + 1 < STEP[o.st] ? 'is-done' : i + 1 === STEP[o.st] ? 'is-current' : ''}">${label}</li>`).join('')}</ol>`;
            const date = o.d ? new Date(o.d).toLocaleDateString(document.documentElement.lang || 'fr') : '';
            return `<article class="db-track__order${o.st === 'refusee' ? ' is-refused' : ''}"><header class="db-track__head"><strong>${this.dataset.order.replace('[n]', B.esc(o.name))}</strong><span>${date}</span></header><ul class="db-track__lines" role="list">${lines}</ul>${body}</article>`;
          })
          .join('');
        this.querySelector('[data-none]').hidden = list.length > 0;
      }
    }
  );
}
